import { useState } from "react";
import { Button, Table, Modal, TextInput } from "flowbite-react";
import { HiPencil, HiTrash, HiPlus, HiSearch } from "react-icons/hi";
import { CategoryCustom } from "../../Models/CategoryCustom";
import { toast } from "react-toastify";
import CommonApi from "../../Api/CommonApi";
import EditCategoryModal from "./EditCategoryModal";

interface CategoryListTableProps {
  data: CategoryCustom[];
  onRefresh: () => Promise<void> | undefined;
}

export default function CategoryListTable({
  data,
  onRefresh,
}: CategoryListTableProps) {
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryCustom | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] =
    useState<CategoryCustom | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter data based on search term and sort by number
  const filteredData = data
    .filter(
      (category) =>
        category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.number?.toString().includes(searchTerm)
    )
    .sort((a, b) => (a.number || 0) - (b.number || 0));

  const handleEdit = (category: CategoryCustom) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleDelete = (category: CategoryCustom) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      try {
        const response = await CommonApi.deleteCategory(
          categoryToDelete.number
        );
        if (response.data.success) {
          toast.success(response.data.message);
          onRefresh();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("Error deleting category");
      }
    }
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCategory(null);
  };

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex-1 max-w-md">
          <TextInput
            id="search"
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={HiSearch}
            sizing="md"
          />
        </div>
        <Button className="buttonMain" onClick={() => setShowEditModal(true)}>
          <HiPlus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.HeadCell>Number</Table.HeadCell>
            <Table.HeadCell>Name</Table.HeadCell>
            <Table.HeadCell>Actions</Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {filteredData.map((category) => (
              <Table.Row
                key={category.number}
                className="bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                  {category.number}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap text-gray-900 dark:text-white">
                  {category.name}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      color="blue"
                      onClick={() => handleEdit(category)}
                    >
                      <HiPencil className="h-4 w-4" />
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      <EditCategoryModal
        show={showEditModal}
        setShow={handleCloseEditModal}
        category={selectedCategory}
        onRefresh={onRefresh}
      />

      <Modal
        show={showDeleteModal}
        size="md"
        onClose={() => setShowDeleteModal(false)}
      >
        <Modal.Header>Confirm Delete</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
              Are you sure you want to delete the category "
              {categoryToDelete?.name}"? This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} color="red">
            Delete
          </Button>
          <Button color="gray" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
